import { callWithAuth, getAllChapters, getAllLessons } from "@/api/Api";
import { GetAllChaptersRequest, GetCourseInfoResponse, GetAllLessonRequest, GetAllLessonsResponse, Lesson } from "@shared_types";

// Map CEFR levels to course IDs
const CEFR_TO_COURSE_MAP: Record<string, number> = {
  "A0": 5, // tik tok course
  "Lower A1": 1, // Beginner 1 course
  "Upper A1": 30, // Beginner 2 course
  "Lower A2": 11, // Beginner 3 course
  "Upper A2": 21, // Beginner 4 course
  "Lower B1": 9, // Intermediate 1 course
  "Upper B1": 9, // Intermediate 1 course
  "Lower B2": 20, // Intermediate 2 course
  "Upper B2": 10, // Intermediate 3 course
};

/**
 * Gets the first chapter and lesson IDs from a course
 * @param courseId The ID of the course to get the first lesson from
 * @returns An object containing the chapter ID and first lesson ID
 */
export const getFirstChapterAndLesson = async (courseId: number): Promise<{ chapterId: number; lessonId: number }> => {
  const request: GetAllChaptersRequest = { courseId };
  const response = await callWithAuth<GetAllChaptersRequest, GetCourseInfoResponse>(getAllChapters, request);
  
  if (!response.chapters || response.chapters.length === 0) {
    throw new Error(`No chapters found for course ${courseId}`);
  }

  // Use the first chapter in the array (assume API returns in order)
  const firstChapter = response.chapters[0];

  // Fetch lessons for the first chapter
  const lessonsRequest: GetAllLessonRequest = { chapterId: firstChapter.chapterId };
  const lessonsResponse = await callWithAuth<GetAllLessonRequest, GetAllLessonsResponse>(getAllLessons, lessonsRequest);

  if (!lessonsResponse.lessons || lessonsResponse.lessons.length === 0) {
    throw new Error(`No lessons found in first chapter of course ${courseId}`);
  }

  // Sort lessons by sequence if available, otherwise use the first
  const sortedLessons = lessonsResponse.lessons.sort((a: Lesson, b: Lesson) => {
    if (typeof a.sequence === 'number' && typeof b.sequence === 'number') {
      return a.sequence - b.sequence;
    }
    return 0;
  });
  return { chapterId: firstChapter.chapterId, lessonId: sortedLessons[0].lessonId };
};

/**
 * Gets the course ID, chapter ID, and first lesson ID for a given CEFR level
 * @param cefrLevel The CEFR level to get the course for
 * @returns An object containing the course ID, chapter ID, and first lesson ID
 */
export const getCourseAndLessonForLevel = async (cefrLevel: string): Promise<{ courseId: number; chapterId: number; lessonId: number }> => {
  const courseId = CEFR_TO_COURSE_MAP[cefrLevel];
  if (!courseId) {
    throw new Error(`No course found for CEFR level ${cefrLevel}`);
  }

  const { chapterId, lessonId } = await getFirstChapterAndLesson(courseId);
  return { courseId, chapterId, lessonId };
}; 